import React from "react";
import { func, string, bool, object, exact } from "prop-types";
import { useForm } from "react-hook-form";
import { useTranslation } from "@lib/i18n";

import EditableContainer from "@components/shared/EditableContainer";
import TextField from "@components/shared/TextField";
import Typography from "@components/shared/Typography";
import { Button } from "@components/shared/Button";

import { getIsCountry, COUNTRIES } from "@lib/getIsCountry";
import { USER_FIELDS_NAMES } from "@shared/constants";

const { ADDRESS } = USER_FIELDS_NAMES;

const Address = ({
  title,
  text = null,
  onIsEditingChange,
  isEditing,
  onSubmit,
  data: address,
  regexPatterns,
}) => {
  const { t } = useTranslation();

  const { register, handleSubmit, reset } = useForm({
    defaultValues: address,
  });

  const isProvinceRelevantForCountry = getIsCountry(COUNTRIES.IT);

  return (
    <EditableContainer
      title={title}
      text={text}
      isEditing={isEditing}
      onChange={onIsEditingChange}
      summary={
        <div>
          <Typography className="text-sm">{address.address}</Typography>

          {address.additionalAddress && (
            <Typography className="text-sm">
              {address.additionalAddress}
            </Typography>
          )}

          <Typography className="text-sm">
            {address.postCode} {address.city}
          </Typography>

          <Typography className="text-sm">{address.country}</Typography>
        </div>
      }
      form={
        <form
          className="flex flex-col items-center w-full"
          onSubmit={handleSubmit(data => {
            onSubmit(data);
            reset(data);
          })}
        >
          <TextField
            required
            fullWidth
            ref={register}
            name={ADDRESS.PRIMARY_ADDRESS}
            label={t(`myDetails.inputs.${ADDRESS.PRIMARY_ADDRESS}`)}
            pattern={regexPatterns[ADDRESS.PRIMARY_ADDRESS]}
            errorHelperMessage={t(
              `myDetails.helperTexts.${ADDRESS.PRIMARY_ADDRESS}`
            )}
            placeholder={t(`myDetails.placeholders.${ADDRESS.PRIMARY_ADDRESS}`)}
          />
          <TextField
            fullWidth
            ref={register}
            name={ADDRESS.ADDITIONAL_ADDRESS}
            label={t(`myDetails.inputs.${ADDRESS.ADDITIONAL_ADDRESS}`)}
            pattern={regexPatterns[ADDRESS.ADDITIONAL_ADDRESS]}
            errorHelperMessage={t(
              `myDetails.helperTexts.${ADDRESS.ADDITIONAL_ADDRESS}`
            )}
            placeholder={t(
              `myDetails.placeholders.${ADDRESS.ADDITIONAL_ADDRESS}`
            )}
          />
          <TextField
            required
            fullWidth
            ref={register}
            name={ADDRESS.CITY}
            label={t(`myDetails.inputs.${ADDRESS.CITY}`)}
            pattern={regexPatterns[ADDRESS.CITY]}
            errorHelperMessage={t(`myDetails.helperTexts.${ADDRESS.CITY}`)}
            placeholder={t(`myDetails.placeholders.${ADDRESS.CITY}`)}
          />
          {isProvinceRelevantForCountry && (
            <TextField
              required
              fullWidth
              ref={register}
              name={ADDRESS.PROVINCE}
              label={t(`myDetails.inputs.${ADDRESS.PROVINCE}`)}
              pattern={regexPatterns[ADDRESS.PROVINCE]}
              placeholder={t(`myDetails.placeholders.${ADDRESS.PROVINCE}`)}
            />
          )}
          <TextField
            required
            fullWidth
            ref={register}
            name={ADDRESS.POST_CODE}
            label={t(`myDetails.inputs.${ADDRESS.POST_CODE}`)}
            pattern={regexPatterns[ADDRESS.POST_CODE]}
            title={t(`myDetails.titles.${ADDRESS.POST_CODE}`)}
            errorHelperMessage={t(`myDetails.helperTexts.${ADDRESS.POST_CODE}`)}
            placeholder={t(`myDetails.placeholders.${ADDRESS.POST_CODE}`)}
          />
          <TextField
            required
            fullWidth
            ref={register}
            name={ADDRESS.COUNTRY}
            label={t(`myDetails.inputs.${ADDRESS.COUNTRY}`)}
            pattern={regexPatterns[ADDRESS.COUNTRY]}
            errorHelperMessage={t(`myDetails.helperTexts.${ADDRESS.COUNTRY}`)}
            placeholder={t(`myDetails.placeholders.${ADDRESS.COUNTRY}`)}
          />
          <Button className="text-sm mt-8 mb-2" size="small" type="submit">
            {t("myDetails.inputs.save")}
          </Button>
        </form>
      }
    />
  );
};

Address.propTypes = {
  onSubmit: func.isRequired,
  regexPatterns: exact({
    [ADDRESS.PRIMARY_ADDRESS]: string,
    [ADDRESS.ADDITIONAL_ADDRESS]: string,
    [ADDRESS.CITY]: string,
    [ADDRESS.POST_CODE]: string,
    [ADDRESS.COUNTRY]: string,
    [ADDRESS.PROVINCE]: string,
  }).isRequired,
  title: string,
  text: string,
  onIsEditingChange: func,
  isEditing: bool,
  fontSizeSummary: string,
  data: object,
};

export default Address;
